import { GridRowId, GridRowModesModel } from '@mui/x-data-grid';
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import { useQueryClient } from '@tanstack/react-query';
import { pick } from 'es-toolkit/compat';
import React from 'react';

import { editAnnotationAssignment } from 'api/annotationAssignments';
import { useCrudControlsColumns } from 'components/atoms/EditableDataGrid/rowEditingControlsColumns';
import { AnnotationAssignment } from 'interfaces/annotation';
import {
  captureAnnotationAssignmentsQuerySnapshots,
  invalidateAnnotationAssignmentsAcrossCaches,
  restoreAnnotationAssignmentsQuerySnapshots,
  updateAnnotationAssignmentsAcrossCaches,
} from 'utils/annotationAssignmentsQueryCache';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import { AnnotationAssignmentRowChangesSummary } from './ChangeSummaries';
import { AnnotationAssignmentFieldContext, annotationAssignmentFields } from './fields';

export const useAnnotationAssignmentsColumns = ({
  apiRef,
  queryKey,
  noRows,
  currentRows,
  rowModesModel,
  isLoading,
  bulkEditMode,
  fieldsContext,
  setRowModesModel,
}: {
  noRows?: boolean;
  queryKey?: any;
  apiRef: React.MutableRefObject<GridApiCommunity>;
  currentRows: AnnotationAssignment[];
  rowModesModel: GridRowModesModel;
  isLoading: boolean;
  bulkEditMode: boolean;
  fieldsContext: AnnotationAssignmentFieldContext;
  setRowModesModel: React.Dispatch<React.SetStateAction<GridRowModesModel>>;
}) => {
  const [mutatingRowId, setMutatingRowId] = React.useState<GridRowId | undefined>();

  const queryClient = useQueryClient();

  const commonMutationOptions = {
    onError: () => {
      setMutatingRowId(undefined);
    },
  };

  const editAnnotationAssignmentMutation = useMutationWithErrorSnackbar({
    ...commonMutationOptions,
    onMutate: (variables) => {
      const snapshots = captureAnnotationAssignmentsQuerySnapshots(queryClient);

      updateAnnotationAssignmentsAcrossCaches(queryClient, (assignment) => {
        if (assignment.annotationAssignmentId === variables.annotationAssignmentId) {
          return { ...assignment, ...variables };
        }

        return assignment;
      });

      return { snapshots };
    },
    onSuccess: () => {
      invalidateAnnotationAssignmentsAcrossCaches(queryClient);
      setMutatingRowId(undefined);
    },
    onError: (error, _variables, context) => {
      restoreAnnotationAssignmentsQuerySnapshots(queryClient, context?.snapshots || []);
      setMutatingRowId(undefined);
    },
    mutationFn: ({ annotationAssignmentId, ...data }: Partial<AnnotationAssignment>) => {
      return editAnnotationAssignment({ annotationAssignmentId, data: pick(data, 'tags') });
    },
    mutationDescription: 'update annotation assignment',
  });

  // @ts-ignore
  const columns = useCrudControlsColumns<AnnotationAssignment, AnnotationAssignment, 'annotationAssignmentId'>({
    isLoading,
    bulkEditMode,
    updateMutation: editAnnotationAssignmentMutation,
    apiRef,
    idField: 'annotationAssignmentId',
    rowModesModel,
    setRowModesModel,
    currentRows,
    rowTypeFields: annotationAssignmentFields,
    noRows,
    getCancelEditConfirmationModalOptions: ({ changes, currentRowValue, newRowValue }) => ({
      title: 'Cancel annotation assignment update',
      text: (
        <AnnotationAssignmentRowChangesSummary
          currentRowValue={currentRowValue}
          newRowValue={newRowValue}
          changes={changes}
          fieldsContext={fieldsContext}
        />
      ),
    }),
    getSaveConfirmationModalOptions: ({ changes, currentRowValue, newRowValue }) => ({
      title: 'Update annotation assignment',
      text: (
        <AnnotationAssignmentRowChangesSummary
          currentRowValue={currentRowValue}
          newRowValue={newRowValue}
          changes={changes}
          fieldsContext={fieldsContext}
        />
      ),
    }),
    mutatingRowId,
    setMutatingRowId,
  });

  return columns;
};
